<template>
  <ImageGallery
    :images="images"
    :initial-index="initialImageIndex"
    :loading="loading"
    :empty-message="t('Nenhuma imagem encontrada para este serviço')"
    :show-thumbnails="true"
    :show-zoom-controls="true"
    :show-fullscreen-control="true"
    :show-delete-button="false"
    data-testid="galeria-image-gallery"
    @close="handleClose"
  />
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useGaleria } from '@/composables/useGaleria';
import { eventBus } from '@/eventBus';
import { log } from '@/utils/DebugLogger';
import type { ImagensServicos_FotoRow } from '@/contracts/ImagensServicos_FotoRow';

interface GalleryImage {
  id: number | string;
  url: string;
  thumbnailUrl: string;
  label: string;
  type: 'antes' | 'depois';
}

// Local alias: the response contract row has optional url/antes_depois
// fields; we narrow at the use-site below with safe defaults.
type ServicoImage = ImagensServicos_FotoRow;

const { t } = useI18n();

const props = defineProps<{
  compromissoServicoId: number | string;
  initialImageIndex?: number;
}>();

const emit = defineEmits<{
  (e: 'close'): void;
}>();

const { fetchAllServicoImages } = useGaleria();

const loading = ref<boolean>(true);
const images = ref<GalleryImage[]>([]);

const loadImages = async (): Promise<void> => {
  log('GaleriaImageGallery', 'loadImages', 'Loading images for service', props.compromissoServicoId);
  loading.value = true;

  try {
    const data: ServicoImage[] = await fetchAllServicoImages(Number(props.compromissoServicoId));
    log('GaleriaImageGallery', 'loadImages', 'Images loaded', data);

    images.value = data.map((img, index) => {
      const isAntes = img.antes_depois === 'a';
      const type: 'antes' | 'depois' = isAntes ? 'antes' : 'depois';

      const countSoFar = data.slice(0, index).filter(i => i.antes_depois === img.antes_depois).length;
      const label = isAntes
        ? (data.filter(i => i.antes_depois === 'a').length > 1 ? `${t('Antes')} ${countSoFar + 1}` : t('Antes'))
        : (data.filter(i => i.antes_depois === 'd').length > 1 ? `${t('Depois')} ${countSoFar + 1}` : t('Depois'));

      return {
        id: img.imagem_id,
        url: img.imagem_url ?? '',
        thumbnailUrl: img.imagem_thumbnail_url ?? '',
        label,
        type
      };
    });

    loading.value = false;
  } catch (error) {
    log('GaleriaImageGallery', 'loadImages', 'Error loading images', error);
    loading.value = false;
    eventBus.emit('globalMessage.error', t('Erro ao carregar imagens do serviço'));
  }
};

const handleClose = (): void => {
  emit('close');
};

onMounted(() => {
  log('GaleriaImageGallery', 'onMounted', 'Component mounted');
  loadImages();
});
</script>
